<template>
  <!-- v-calendarの型が中途半端でAttibutesの型が死ぬので、componentを利用して型を落としている -->
  <component
    :is="Calendar"
    is-expanded
    :masks="{ title: 'YYYY年 M月' }"
    :attributes="attributes"
    class="calendar"
    :class="$style.calendar"
    @dayclick="clickAdd"
  >
    <template #header="{ title }">
      <p :class="$style.calendar__header">{{ title }}</p>
    </template>
    <template #day-content="{ day, attributes, dayEvents }">
      <div
        :class="[
          $style.calendarCell,
          { [$style['calendarCell--editable']]: checkEditable(day) },
        ]"
        @click="dayEvents.click"
      >
        <div
          :class="[
            $style.calendarCell__header,
            { [$style['calendarCell__header--disabled']]: !day.inMonth },
          ]"
        >
          <p :class="$style.calendarCell__title">{{ getDayString(day) }}</p>
          <div :class="$style.calendarCell__add">
            <IconAddCircle :class="$style.calendarCell__icon" />
            <p>追加</p>
          </div>
        </div>
        <div :class="$style.calendarCell__body">
          <div
            v-for="attribute in attributes"
            :key="attribute.key"
            :class="[$style.calendarItem]"
            @click.stop="$emit('select', attribute.customData.id)"
          >
            <p>
              {{
                getItemTitle(
                  day.date,
                  attribute.customData.startedAt,
                  attribute.customData.endedAt
                )
              }}
            </p>
            <div :class="[$style.calendarItem__capacity]">
              <IconPerson :class="[$style.calendarItem__icon]" />
              <p>
                <template v-if="attribute.customData.isReachedCapacity"
                  >満員</template
                >
                <template v-else
                  >{{ attribute.customData.applicantCount }}人</template
                >
              </p>
            </div>
          </div>
        </div>
      </div>
    </template>
  </component>
</template>

<script lang="ts" setup>
import { Calendar } from 'v-calendar'
import IconPerson from '@/components/icons/IconPerson.vue'
import 'v-calendar/dist/style.css'
import { format, parseISO, startOfDay, endOfDay } from 'date-fns'
import IconAddCircle from '@/components/icons/IconAddCircle.vue'
import type { SeminarCalendarScheduleFragment } from './SeminarCalendar.generated'
import { computed } from 'vue'
import { fromTimeString } from '@/libs/utilities/gql-scalar'

const emit = defineEmits<{
  (event: 'add', date: Date): void
  (event: 'select', id: SeminarCalendarScheduleFragment['id']): void
}>()

const today = startOfDay(new Date())

const checkEditable = ({ date }: { date: Date }) => {
  return today <= date
}

const props = defineProps<{
  title: string
  schedules: SeminarCalendarScheduleFragment[]
}>()

const getDayString = ({
  day,
  isFirstDay,
  date,
}: {
  day: string
  date: Date
  isFirstDay: boolean
}): string => (isFirstDay ? format(date, 'M月d日') : day)

const getItemTitle = (
  targetDate: Date,
  startedAtString: TimeString,
  endedAtString: TimeString
): string => {
  const startedAt = fromTimeString(startedAtString)
  const endedAt = fromTimeString(endedAtString)

  const startOfTargetDay = startOfDay(targetDate)
  const endOfTargetDay = endOfDay(targetDate)

  const isInDay = startOfTargetDay <= startedAt && endedAt <= endOfTargetDay

  const formatKey = isInDay ? 'HH:mm' : 'MM/dd HH:mm'

  return `${format(startedAt, formatKey)}~${format(endedAt, formatKey)}`
}

type ScheduleData = Pick<
  SeminarCalendarScheduleFragment,
  'id' | 'isReachedCapacity' | 'applicantCount' | 'startedAt' | 'endedAt'
>

type Attribute = {
  key: string
  customData: ScheduleData
  dates: { start: Date; end: Date }
}

const clickAdd = ({ date }: { date: Date }) => {
  if (checkEditable({ date })) {
    emit('add', date)
  }
}

const attributes = computed(() =>
  props.schedules.map(
    ({
      id,
      isReachedCapacity,
      applicantCount,
      startedAt,
      endedAt,
    }): Attribute => {
      return {
        key: id,
        customData: {
          id,
          isReachedCapacity,
          applicantCount,
          startedAt,
          endedAt,
        },
        dates: {
          start: parseISO(startedAt),
          end: parseISO(endedAt),
        },
      }
    }
  )
)
</script>

<style lang="scss" module>
.calendar {
  width: 100%;
  $border: 1px solid $color-border-low-emphasis;

  &__header {
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 10px 0;
    font-weight: bold;
    font-size: 16px;
    background-color: $color-surface-accent-primary-light;

    // v-calendar側のstyleのborderと合わせる必要がある
    border-radius: 0.5rem 0.5rem 0 0;
  }

  // stylelint-disable selector-class-pattern
  :global(.vc-header) {
    margin-bottom: 8px;
  }

  :global(.vc-weeks) {
    padding: 0;
  }

  :global(.vc-weekday) {
    border-top: $border;
    border-bottom: $border;
    padding: 5px 0;
    font-weight: bold;
    color: $color-text-and-object-high-emphasis;

    &:first-child {
      color: $color-label-red;
    }

    &:nth-child(7) {
      color: $color-label-blue;
    }
  }

  // stylelint-disable-next-line no-descending-specificity
  :global(.vc-day) {
    text-align: center;
    height: 110px;
    min-width: 90px;
    padding: 6px 4px;

    &.is-not-in-month * {
      opacity: 1;
    }

    &:not(.on-bottom) {
      border-bottom: $border;

      &.weekday-1 {
        border-bottom: $border;
      }
    }

    &:not(.on-right) {
      border-right: $border;
    }
  }
  // stylelint-enable selector-class-pattern
}

.calendarCell {
  $root: &;

  display: flex;
  flex-direction: column;
  height: 100%;

  &__header {
    padding: 0 10px;
    font-weight: bold;
    position: relative;
    box-sizing: border-box;

    &--disabled {
      color: $color-text-and-object-low-emphasis;
    }
  }

  &__title {
    min-width: fit-content;
  }

  &__body {
    margin-top: 10px;
    flex-grow: 1;
    overflow: scroll;
  }

  &__add {
    color: $color-text-and-object-accent-primary;
    display: none;
  }

  &__icon {
    $size: 20px;

    width: $size;
    height: $size;
  }

  &--editable {
    &:hover {
      cursor: pointer;

      #{$root}__header {
        text-align: left;
        display: flex;
        justify-content: center;
        align-items: center;

        & > *:first-child {
          animation: sideway-appear 0.2s linear;
          flex: 1;

          @keyframes sideway-appear {
            0% {
              flex: 0;
            }

            100% {
              flex: 1;
            }
          }
        }
      }

      #{$root}__add {
        position: absolute;
        right: 10px;
        display: flex;
        justify-content: right;
        align-items: center;
        animation: add-appear 0.2s linear;

        @keyframes add-appear {
          0% {
            opacity: 0;
          }

          100% {
            opacity: 1;
          }
        }
      }
    }
  }
}

.calendarItem {
  border: 1px solid $color-border-low-emphasis;
  border-radius: 4px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 10px;
  padding: 2px 4px;

  &:nth-child(n + 2) {
    margin-top: 4px;
  }

  &__capacity {
    display: flex;
    align-items: center;
    color: $color-caution-accent;
  }

  &__icon {
    $size: 14px;

    width: $size;
    height: $size;
  }

  &:hover {
    background-color: $color-surface-accent-primary-light;
  }
}
</style>
